/**
 * Types and constants for Schedule Lines Tab components.
 *
 * Mirrors the PO Lines types.ts pattern with column config,
 * filter state, and status option definitions.
 */
import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import { ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE_SHORT } from '../../../../config/dateLabels';

// Column configuration for visibility management
export interface ScheduleColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  minWidth: string;
  order: number;
  /** User-resized width in pixels. Falls back to ``minWidth`` when unset. */
  width?: number;
}

export const DEFAULT_SCHEDULE_COLUMNS: ScheduleColumnConfig[] = [
  // EXPEDITING-PARITY PR 2 (Aaron, 17 Sep 2026), the same two changes the PO
  // line grid got: "Ack Status" reads as jargon and is just Status, and the
  // separate On Hold column RETIRES into it because a held row's status is the
  // hold. The fulfilment column further down was also called "Status" — two
  // columns claiming the same word on one grid — so it is now Delivery Status,
  // which is what its values (open / shipped / cancelled / missed) actually say.
  { id: 'acknowledge_status', label: 'Status', visible: true, sortable: true, minWidth: '170px', order: 0 },
  // Six icon buttons at most (accept, decline, propose date, propose qty,
  // hold, close) — sized so they never crowd the Status pills beside them.
  { id: 'action', label: 'Action', visible: true, sortable: false, minWidth: '190px', order: 1 },
  // Why a line is an exception, and — when it is held — the words whoever
  // held it typed. Both sit next to On Hold so the answer is beside the flag.
  // Off by default everywhere; the Exceptions tab turns the reason on for
  // itself, since a tab of exceptions that does not say why is a to-do list
  // you have to open every row of.
  { id: 'exception_reason', label: 'Exception Reason', visible: false, sortable: false, minWidth: '170px', order: 2.3 },
  { id: 'on_hold_reason', label: 'On Hold Reason', visible: false, sortable: false, minWidth: '200px', order: 2.6 },
  { id: 'waiting_on', label: 'Waiting On', visible: true, sortable: true, minWidth: '110px', order: 3 },
  { id: 'po_number', label: 'PO Number', visible: true, sortable: true, minWidth: '130px', order: 4 },
  { id: 'line_number', label: 'Line #', visible: true, sortable: true, minWidth: '60px', order: 5 },
  { id: 'schedule_line_number', label: 'Sched Line', visible: true, sortable: true, minWidth: '90px', order: 6 },
  { id: 'erp_order_type', label: 'ERP Order Type', visible: true, sortable: false, minWidth: '120px', order: 6.5 },
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, minWidth: '150px', order: 7 },
  { id: 'supplier_part_number', label: 'Supplier Part No', visible: true, sortable: false, minWidth: '130px', order: 7.5 },
  { id: 'part_description', label: 'Part Description', visible: true, sortable: false, minWidth: '200px', order: 7.25 },
  { id: 'supplier_name', label: 'Supplier', visible: true, sortable: true, minWidth: '180px', order: 9 },
  // ERP buyer code of the line. Customer users only, offered where ERP Balance Qty is (ScheduleLinesTable).
  { id: 'buyer_code', label: 'Buyer Code', visible: true, sortable: true, minWidth: '100px', order: 9.5 },
  { id: 'warehouse', label: 'Warehouse', visible: true, sortable: false, minWidth: '120px', order: 10 },
  { id: 'customer_name', label: 'Company', visible: false, sortable: true, minWidth: '150px', order: 11 },
  { id: 'schedule_date', label: 'Schedule Date', visible: true, sortable: true, minWidth: '130px', order: 12 },
  { id: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE_SHORT, visible: true, sortable: true, minWidth: '130px', order: 13 },
  { id: 'original_promise_date', label: ORIGINAL_REQUIRED_DATE, visible: false, sortable: true, minWidth: '140px', order: 14 },
  { id: 'schedule_qty', label: 'Sched Qty', visible: true, sortable: true, minWidth: '100px', order: 15 },
  { id: 'shipped_qty', label: 'Received', visible: true, sortable: true, minWidth: '90px', order: 16 },
  { id: 'balance_qty', label: 'Balance', visible: true, sortable: true, minWidth: '90px', order: 17 },
  // ERP-reported open qty. Only offered to DE customer users (ScheduleLinesTable).
  { id: 'erp_balance_qty', label: 'ERP Balance Qty', visible: true, sortable: true, minWidth: '120px', order: 17.5 },
  { id: 'unit_of_measure', label: 'UOM', visible: false, sortable: true, minWidth: '70px', order: 18 },
  { id: 'unit_price', label: 'Unit Price', visible: false, sortable: true, minWidth: '110px', order: 19 },
  { id: 'status', label: 'Delivery Status', visible: true, sortable: true, minWidth: '130px', order: 20 },
  { id: 'updated_at', label: 'Updated', visible: false, sortable: true, minWidth: '140px', order: 21 },
];

export const SCHEDULE_COLUMN_STORAGE_KEY = 'schedule_lines_column_config';

/** Acknowledge states in which a proposal is still open — the ball is with one
 *  side or the other. Outside these the proposal fields are stale: accepting or
 *  declining clears them (ScheduleWorkflowService), but a row that never had a
 *  proposal has them empty anyway, so gating on status keeps the column honest
 *  on the tabs where it can also be switched on. */
const OPEN_CHANGE_STATUSES = new Set([
  'change_proposed_supplier',
  'change_proposed_customer',
  'change_declined',
  'queried',
]);

/**
 * What makes this schedule line an exception, in the words the register uses.
 *
 * Returns the parts joined with " + " — a line can be both a date and a
 * quantity proposal, and can additionally be on hold. Empty string when the
 * line is ordinary, which the cell renders as a dash.
 *
 * Derived rather than stored: the workflow already records the proposal on
 * ``supplier_proposed_date`` / ``supplier_proposed_qty`` and the hold on
 * ``on_hold``, so a stored reason would be a second copy to keep in step.
 */
export function describeExceptionReason(s: POLineSchedule): string {
  const parts: string[] = [];
  const openChange = OPEN_CHANGE_STATUSES.has(s.acknowledge_status ?? '');

  if (openChange && s.supplier_proposed_date && s.supplier_proposed_date !== s.schedule_date) {
    parts.push('Date Change');
  }
  if (openChange && s.supplier_proposed_qty != null
      && parseFloat(s.supplier_proposed_qty) !== parseFloat(s.schedule_qty)) {
    parts.push('Qty Change');
  }
  // A query is an exception in its own right — the supplier has asked a
  // question rather than proposed a value, so neither field above is set.
  if (s.acknowledge_status === 'queried' && parts.length === 0) parts.push('Query');
  // Declined with nothing else to show: the change itself was refused.
  if (s.acknowledge_status === 'change_declined' && parts.length === 0) parts.push('Change Declined');
  if (s.on_hold) parts.push('On Hold');

  return parts.join(' + ');
}

// Filter state interface
export interface ScheduleFilters {
  poNumber: string;
  lineNumber: string;
  scheduleLineNumber: string;
  partNumber: string;
  supplierPartNumber: string;
  /** Toolbar quick search — matches part number OR supplier part no. */
  partSearch: string;
  supplier: string;
  warehouse: string;
  erpOrderType: string;
  acknowledgeStatus: string;
  scheduleStatus: string;
  onHold: string;
  scheduleDateFrom: string;
  scheduleDateTo: string;
  promiseDateFrom: string;
  promiseDateTo: string;
  /** OTIF exclusion (PO or line flagged ``exclude_from_otif``):
   *  '' = show all, 'hide' = drop excluded rows, 'only' = excluded rows only. */
  otifExcluded: string;
}

export const DEFAULT_SCHEDULE_FILTERS: ScheduleFilters = {
  poNumber: '',
  lineNumber: '',
  scheduleLineNumber: '',
  partNumber: '',
  supplierPartNumber: '',
  partSearch: '',
  supplier: '',
  warehouse: '',
  erpOrderType: '',
  acknowledgeStatus: '',
  scheduleStatus: '',
  onHold: '',
  scheduleDateFrom: '',
  scheduleDateTo: '',
  promiseDateFrom: '',
  promiseDateTo: '',
  otifExcluded: '',
};

/**
 * Filters a schedule-line tenant's Scheduled Lines page opens with. Shown as
 * removable chips, never silently: scheduling agreements (ERP type 4) are the
 * planner's population, and OTIF-excluded orders are kept out so the list
 * matches what the Scheduled Dashboard scores. Clearing a chip restores the
 * unfiltered list for the session; the next visit starts from the defaults.
 */
export const SCHEDULE_TENANT_DEFAULT_FILTERS: Partial<ScheduleFilters> = {
  erpOrderType: '4',
  otifExcluded: 'hide',
};

/** Human labels for the active-filter chips. */
export const SCHEDULE_FILTER_LABELS: Record<keyof ScheduleFilters, string> = {
  poNumber: 'PO',
  lineNumber: 'Line',
  scheduleLineNumber: 'Sched line',
  partNumber: 'Part',
  supplierPartNumber: 'Supplier part',
  partSearch: 'Search',
  supplier: 'Supplier',
  warehouse: 'Warehouse',
  erpOrderType: 'ERP order type',
  acknowledgeStatus: 'Ack status',
  scheduleStatus: 'Schedule status',
  onHold: 'On hold',
  scheduleDateFrom: 'Schedule date from',
  scheduleDateTo: 'Schedule date to',
  promiseDateFrom: 'Promise date from',
  promiseDateTo: 'Promise date to',
  otifExcluded: 'OTIF-excluded lines',
};

/** Display value for a chip — the option label where one exists, else the raw value. */
export function describeFilterValue(key: keyof ScheduleFilters, value: string): string {
  if (key === 'erpOrderType') {
    return ERP_ORDER_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
  }
  if (key === 'otifExcluded') return value === 'hide' ? 'hidden' : value === 'only' ? 'only' : value;
  if (key === 'onHold') return value === 'yes' ? 'yes' : value === 'no' ? 'no' : value;
  return value;
}

// Warehouse filter options (display names mapped backend-side from plant codes).
export const WAREHOUSE_OPTIONS = [
  { value: 'Warwick', label: 'Warwick' },
  { value: 'Blackpool', label: 'Blackpool' },
];

export const ACKNOWLEDGE_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'accepted', label: 'WIP (Accepted)' },
  { value: 'declined', label: 'Declined' },
  { value: 'change_proposed_supplier', label: 'Change Proposed (Supplier)' },
  { value: 'change_proposed_customer', label: 'Change Proposed (Customer)' },
  { value: 'change_declined', label: 'Change Declined' },
  { value: 'wip', label: 'WIP (Legacy)' },
  { value: 'despatched', label: 'Dispatched' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'closed', label: 'Closed' },
  // Pseudo-status: rows whose delivery date falls outside the firm
  // window. Backed by is_forecast filter rather than acknowledge_status.
  { value: 'forecast', label: 'Forecast' },
  // Pseudo-status: the date passed without full delivery. Backed by the
  // schedule status, because acknowledge_status is never 'missed' — the ERP
  // never acknowledges, so those rows sit at 'new'. Listed here so the Ack
  // Status column's "Missed" pill is filterable from the control beside it.
  { value: 'missed', label: 'Missed' },
];

// Raw ERP order-type codes (DE: 4 = scheduling agreement, 1 = standard/spot).
export const ERP_ORDER_TYPE_OPTIONS = [
  { value: '4', label: 'Scheduling Agreement (4)' },
  { value: '1', label: 'Standard / Spot (1)' },
  { value: '2', label: 'Type 2' },
  { value: '3', label: 'Type 3' },
];

export const SCHEDULE_STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'partially_shipped', label: 'Partially Received' },
  { value: 'shipped', label: 'Received' },
  { value: 'missed', label: 'Missed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Acknowledge status color mapping (matches PO Lines pattern)
export const ACK_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-yellow-100 text-yellow-800' },
  // Customer review 2026-07-08: an accepted line IS work-in-progress on the
  // order book (legacy accepted->WIP lifecycle collapsed into one state), so
  // display it as WIP — matches the Excel export which already labels both
  // 'accepted' and legacy 'wip' rows as WIP.
  accepted: { label: 'WIP', className: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-800' },
  change_declined: { label: 'Change Declined', className: 'bg-red-100 text-red-800' },
  change_proposed_supplier: { label: 'Changed (Supplier)', className: 'bg-blue-100 text-blue-800' },
  change_proposed_customer: { label: 'Changed (Customer)', className: 'bg-blue-100 text-blue-800' },
  wip: { label: 'WIP', className: 'bg-purple-100 text-purple-800' },
  despatched: { label: 'Dispatched', className: 'bg-teal-100 text-teal-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  queried: { label: 'Queried', className: 'bg-orange-100 text-orange-800' },
  closed: { label: 'Closed', className: 'bg-gray-100 text-gray-500' },
};

// Schedule status color mapping
export const SCHEDULE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-800' },
  // DE: the single current delivery target. Bold + solid colour so it stands out.
  active: { label: 'Active', className: 'bg-emerald-600 text-white font-bold ring-1 ring-emerald-700' },
  partially_shipped: { label: 'Partially Received', className: 'bg-orange-100 text-orange-800' },
  shipped: { label: 'Received', className: 'bg-green-100 text-green-800' },
  // DE: active date passed without full delivery (arrears).
  missed: { label: 'Missed', className: 'bg-rose-600 text-white font-bold' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

/** Tab values for schedule lines, matching PO Lines parity. */
/**
 * Statuses on which schedule collaboration is over.
 *
 * `shipped` and `cancelled` are finished work. `missed` joins them because the
 * delivery window has passed with the line still outstanding: accepting a date
 * that is already in the past, or declining one, says nothing anybody can act
 * on, and the row reads as though a response is still expected. Arrears are
 * resolved by a receipt landing or by the ERP re-dating the line — not by the
 * two sides agreeing about a date gone by.
 *
 * Display is unaffected: a missed row still shows its own "Missed" badge, not
 * "Closed". The backend enforces the same set in ScheduleWorkflowService, so
 * hiding the controls is presentation, never the permission itself.
 */
export const COLLAB_CLOSED_STATUSES = ['shipped', 'cancelled', 'missed'] as const;
// NOTE: the bulk bar deliberately keeps 'shipped' selectable — a shipped line
// can still carry an open date change to answer — so it excludes 'cancelled'
// and 'missed' by name rather than using this set.

/** Whether collaboration actions (accept / decline / propose) are over. */
export function isCollabClosed(s: Pick<POLineSchedule, 'status'>): boolean {
  return (COLLAB_CLOSED_STATUSES as readonly string[]).includes(s.status);
}

export type ScheduleTab = 'all' | 'needs_action' | 'due_previous_weeks' | 'due_this_week' | 'due_next_week' | 'open' | 'closed' | 'on_hold' | 'forecasted';

// Re-export for convenience
export type { POLineSchedule };
